import { useMemo } from 'react';

import type { FragmentType } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import type { ServiceUsagesTotalCardCmp_TotalCalculation_Fragment } from '../../../components/ServiceUsages/TotalCard';

import { FormVariant } from '../../enums/variant.enum';
import { QuotationHeaderForm } from '../../QuotationHeader';

import { ResolvableMissionTypeFormTab } from '../enums/tab.enum';
import { ResolvableMissionTypeInfoFormStep } from '../steps/Info';
import { ResolvableMissionTypeTemplatesFormStep } from '../steps/Templates';
import { ResolvableMissionTypeServiceUsagesFormStep } from '../steps/ServiceUsages';
import { ResolvableMissionTypeFilesFormStep } from '../steps/Files';
import { ResolvableMissionTypeToolkitsFormStep } from '../steps/Toolkits';
import { ResolvableMissionTypeInfoSchema } from '../schemas/info.schema';
import { ResolvableMissionTypeTemplatesSchema } from '../schemas/templates.schema';
import { ResolvableMissionTypeFilesSchema } from '../schemas/files.schema';
import { ResolvableMissionTypeToolkitsSchema } from '../schemas/toolkits.schema';
import { ResolvableMissionTypeQuotationHeaderSchema } from '../schemas/quotation-header.schema';
import type { ResolvableMissionTypeForm_ResolvableMissionType } from '../fragments/resolvable-mission-type.fragment';

import { useResolvableMissionTypeServiceUsagesSchema } from './useServiceUsagesSchema.hook';

interface Params {
  readonly formVariant: FormVariant;
  readonly initialValues: ResolvableMissionTypeForm_ResolvableMissionType;
  readonly stepIdsSubset?: readonly ResolvableMissionTypeFormTab[];
  readonly totalCalculation?: FragmentType<
    typeof ServiceUsagesTotalCardCmp_TotalCalculation_Fragment
  > | null;
}

export const useResolvableMissionTypeSteps = ({
  formVariant,
  initialValues,
  stepIdsSubset,
  totalCalculation,
}: Readonly<Params>) => {
  const {
    fileDirectoryCategoriesCodeMap,
    resolvableMissionTypeFileCategoriesCodeMap,
  } = useReferencesContext();

  const ResolvableMissionTypeServiceUsagesSchema =
    useResolvableMissionTypeServiceUsagesSchema();

  const { steps, wizardChildren } = useMemo(() => {
    const storage = {
      directoryCategoryId:
        fileDirectoryCategoriesCodeMap.ResolvableMissionType._id,
      entityId: initialValues._id,
      imageCategory:
        resolvableMissionTypeFileCategoriesCodeMap.TextEditorImages,
    };

    const resolversSettings = {
      allowedReturnTypeIds: undefined,
      enableDirectGeneration: false,
      mustBeResolvableOnBack: false,
    };

    const generalInfo = {
      step: {
        id: ResolvableMissionTypeFormTab.GeneralInfo,
        isEnabled: true,
        title: 'Informations Type de Mission',
        ValidationSchema: ResolvableMissionTypeInfoSchema,
      },
      child: (
        <ResolvableMissionTypeInfoFormStep
          id={ResolvableMissionTypeFormTab.GeneralInfo}
          key={ResolvableMissionTypeFormTab.GeneralInfo}
          resolversSettings={resolversSettings}
          storage={storage}
        />
      ),
    };

    const templates = {
      step: {
        id: ResolvableMissionTypeFormTab.Templates,
        isEnabled: true,
        title: 'Modèles',
        ValidationSchema: ResolvableMissionTypeTemplatesSchema,
      },
      child: (
        <ResolvableMissionTypeTemplatesFormStep
          id={ResolvableMissionTypeFormTab.Templates}
          key={ResolvableMissionTypeFormTab.Templates}
        />
      ),
    };

    const quotationHeader = {
      step: {
        id: ResolvableMissionTypeFormTab.QuotationHeader,
        isEnabled: true,
        title: 'Entête de devis',
        ValidationSchema: ResolvableMissionTypeQuotationHeaderSchema,
      },
      child: (
        <QuotationHeaderForm
          formContext="quotationHeader"
          id={ResolvableMissionTypeFormTab.QuotationHeader}
          key={ResolvableMissionTypeFormTab.QuotationHeader}
          resolversSettings={resolversSettings}
          storage={storage}
        />
      ),
    };

    const serviceUsages = {
      step: {
        id: ResolvableMissionTypeFormTab.ServiceUsages,
        isEnabled: true,
        title: 'Prestations',
        ValidationSchema: ResolvableMissionTypeServiceUsagesSchema,
      },
      child: (
        <ResolvableMissionTypeServiceUsagesFormStep
          id={ResolvableMissionTypeFormTab.ServiceUsages}
          key={ResolvableMissionTypeFormTab.ServiceUsages}
          totalCalculation={totalCalculation}
          useDirectMutation={formVariant === FormVariant.Modification}
        />
      ),
    };

    const files = {
      step: {
        id: ResolvableMissionTypeFormTab.Files,
        isEnabled: true,
        title: 'Fichiers',
        ValidationSchema: ResolvableMissionTypeFilesSchema,
      },
      child: (
        <ResolvableMissionTypeFilesFormStep
          id={ResolvableMissionTypeFormTab.Files}
          key={ResolvableMissionTypeFormTab.Files}
        />
      ),
    };

    const toolkits = {
      step: {
        id: ResolvableMissionTypeFormTab.Toolkits,
        isEnabled: true,
        title: 'Outils',
        ValidationSchema: ResolvableMissionTypeToolkitsSchema,
      },
      child: (
        <ResolvableMissionTypeToolkitsFormStep
          id={ResolvableMissionTypeFormTab.Toolkits}
          key={ResolvableMissionTypeFormTab.Toolkits}
        />
      ),
    };

    const allTabs = [
      [ResolvableMissionTypeFormTab.GeneralInfo, generalInfo],
      [ResolvableMissionTypeFormTab.Templates, templates],
      [ResolvableMissionTypeFormTab.QuotationHeader, quotationHeader],
      [ResolvableMissionTypeFormTab.ServiceUsages, serviceUsages],
      [ResolvableMissionTypeFormTab.Files, files],
      [ResolvableMissionTypeFormTab.Toolkits, toolkits],
    ] as const;

    const activeTabs = stepIdsSubset?.length
      ? allTabs.filter(([tab]) => stepIdsSubset.includes(tab))
      : allTabs;

    const steps = activeTabs.map(([, { step }]) => step);
    const wizardChildren = activeTabs.map(([, { child }]) => child);

    return { steps, wizardChildren };
  }, [
    formVariant,
    initialValues,
    fileDirectoryCategoriesCodeMap,
    resolvableMissionTypeFileCategoriesCodeMap,
    ResolvableMissionTypeServiceUsagesSchema,
    stepIdsSubset,
    totalCalculation,
  ]);

  return { steps, wizardChildren };
};
